import {createPortal} from 'react-dom';
import {pluralize} from '~/common/utils';
import {ResponsiveModal} from '~library/organisms/ResponsiveModal';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {TrashIcon} from '~library/svg/TrashIcon';
import {StarLineIcon} from '~library/svg/v2/StarLineIcon';
import {FeaturedLabel} from './PhotoGalleryTile';

export const PhotoFeatureConfirmationModal = ({onClose, onApprove}) =>
  createPortal(
    <ResponsiveModal
      onClose={onClose}
      title="Feature this photo?"
      body="This photo will replace the current featured photo."
      primaryAction={onApprove}
      primaryText="Feature"
      titleQaId="photos-modal-title"
      bodyQaId="photos-modal-body"
      primaryQaId="photos-modal-action"
      secondaryText="Cancel"
      secondaryAction={onClose}
      isOpen
      overlayClassName="photo-modal__overlay"
    />,
    document.body
  );

export const PhotoContextMenu = ({
  photo,
  isHostModule,
  onClose,
  onFeaturePhoto,
  onDeletePhoto,
  deleting,
}) =>
  createPortal(
    <>
      <Button
        variant="unstyled-button"
        className="photo-context-menu__overlay"
        data-qa-id="photo-context-menu-overlay"
        onClick={onClose}
        aria-label="Close context menu"
      />
      <div className="photo-context-menu">
        <div className="photo-context-menu__header">
          <Button variant="unstyled-button" data-qa-id="close-context-menu" onClick={onClose}>
            <CloseLineIcon ratio={0.75} />
          </Button>
        </div>
        <div className="photo-context-menu__body">
          <div
            style={{
              backgroundImage: `url(${photo.src})`,
            }}
            className="photo-context-menu__photo"
          >
            {photo.isFeatured && <FeaturedLabel isContextMenu />}
          </div>
          <Typography variant="paragraph2">Selected photo</Typography>
        </div>
        <div className="photo-context-menu__actions">
          {!photo.isFeatured && isHostModule && (
            <Button
              variant="secondary"
              size="large"
              data-qa-id="feature-photo"
              onClick={onFeaturePhoto}
              disabled={deleting}
              wide
            >
              <StarLineIcon ratio={0.5} />
              <Typography variant="paragraph2">Feature this photo</Typography>
            </Button>
          )}
          <Button
            variant="secondary"
            size="large"
            data-qa-id="delete-photo"
            onClick={onDeletePhoto}
            wide
          >
            <TrashIcon ratio={0.5} />
            <Typography variant="paragraph2">Delete</Typography>
          </Button>
        </div>
      </div>
    </>,
    document.body
  );

export const PhotoDeleteConfirmationModal = ({onClose, onApprove, isHostModule}) =>
  createPortal(
    <ResponsiveModal
      onClose={onClose}
      title="Delete photo?"
      body={
        isHostModule
          ? 'This will remove your photo from your gallery.'
          : 'This photo won’t be added to the shared album.'
      }
      primaryAction={onApprove}
      primaryText="Delete"
      titleQaId="photos-modal-title"
      bodyQaId="photos-modal-body"
      primaryQaId="photos-modal-action"
      secondaryText="Cancel"
      secondaryAction={onClose}
      isOpen
      overlayClassName="photo-modal__overlay"
    />,
    document.body
  );

export const PhotoDeleteAllConfirmationModal = ({onClose, onApprove, photoCount, isHostModule}) =>
  createPortal(
    <ResponsiveModal
      onClose={onClose}
      title={`Delete ${pluralize('photo', photoCount)}?`}
      body={
        isHostModule
          ? 'This will remove all your photos from your gallery. Your guests won’t see this gallery until you add photos again.'
          : 'These photos won’t be added to the shared album.'
      }
      primaryAction={onApprove}
      primaryText="Delete"
      titleQaId="photos-modal-title"
      bodyQaId="photos-modal-body"
      primaryQaId="photos-modal-action"
      secondaryText="Cancel"
      secondaryAction={onClose}
      isOpen
      overlayClassName="photo-modal__overlay"
    />,
    document.body
  );

export const PhotoDeleteFlagModal = ({onClose}) =>
  createPortal(
    <ResponsiveModal
      onClose={onClose}
      title="Not loving this photo?"
      body="Only the host can delete photos. Let them know if you have any concerns."
      primaryAction={onClose}
      primaryText="Got it"
      titleQaId="photos-modal-title"
      bodyQaId="photos-modal-body"
      primaryQaId="photos-modal-action"
      showSecondary={false}
      isOpen
      overlayClassName="photo-modal__overlay"
    />,
    document.body
  );

export const NotOnGuestListModal = ({onClose}) =>
  createPortal(
    <ResponsiveModal
      onClose={onClose}
      title="Only guests can share photos"
      body="The account you’re logged into isn’t on the guest list for this event. Try a different email or reach out to your host."
      primaryAction={onClose}
      primaryText="Got it"
      titleQaId="photos-modal-title"
      bodyQaId="photos-modal-body"
      primaryQaId="photos-modal-action"
      showSecondary={false}
      isOpen
      overlayClassName="photo-modal__overlay"
    />,
    document.body
  );

export const TooManyPhotosSelectedModal = ({onClose}) => {
  const FILE_COUNT_UPLOAD_LIMIT = evite.features.get('photo_sharing_max_photos_per_user_per_event');
  return createPortal(
    <ResponsiveModal
      onClose={onClose}
      title="Too many photos selected"
      body={`You can only upload ${FILE_COUNT_UPLOAD_LIMIT} photos at a time`}
      primaryAction={onClose}
      primaryText="Got it"
      titleQaId="photos-modal-title"
      bodyQaId="photos-modal-body"
      primaryQaId="photos-modal-action"
      showSecondary={false}
      isOpen
      overlayClassName="photo-modal__overlay"
    />,
    document.body
  );
};
